/**
 * DirmShaftPattern — SHAFT layout renderer (pattern_id = 1)
 *
 * Full elevator experience: background image, glowing panel, floor-grouped buttons.
 * Reads from pre-built directory-navigation-manifest.json (no runtime API calls).
 *
 * Visual design sourced from dirs_tgc_10216_tgc_elevator_dirs (the original
 * runtime elevator). This version is manifest-driven and serves as the shared
 * template for ALL SHAFT-patterned directories.
 *
 * Props: seq (Component_Sequence_Num) → resolves manifest dir → renders elevator
 *
 * Created: 2026-03-22
 * Upgraded: 2026-04-03 — full 10216 visuals, manifest-driven
 */

// @architect-approved: HARDCODED_COLORS - Elevator visual design requires translucent overlays and cyan accents
import RouteIcon from '@mui/icons-material/Route';
import { Box, Button, Paper, Tooltip, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Using directory access background image (same as floor access pattern)
import elevatorBackgroundImage from '../../img/bg_tgc_dir_17.png';
import { handleManifestButtonClick } from '../../../utils/directoryNavigation';
import { useNavigationChrome } from '../../../utils/useNavigationChrome';
import { useBrandingContext } from '../../../StylesLibrary/hooks/useBrandingContext';
import { FLOOR_COLORS } from './icon-map';
import { useDirectoryHost } from './useDirectoryHost';

/**
 * Generate a family of color gradations from a base color
 * Creates lighter and darker variants using HSL manipulation
 */
function generateColorFamily(baseColor: string, count: number = 10): string[] {
    // Parse hex color to RGB
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    // Convert RGB to HSL
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
        }
    }
    
    // Generate color family with varying lightness and saturation
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
        // Create gradation: lighter at start, darker at end
        const lightnessOffset = ((count - 1 - i) / (count - 1)) * 0.25 - 0.125; // Range: +0.125 to -0.125
        const saturationOffset = (i / (count - 1)) * 0.1; // Slightly increase saturation
        
        const newL = Math.max(0.2, Math.min(0.8, l + lightnessOffset));
        const newS = Math.max(0.3, Math.min(1.0, s + saturationOffset));
        
        colors.push(hslToHex(h, newS, newL));
    }
    
    return colors;
}

/**
 * Convert HSL to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
    const hueToRgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
    };
    
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hueToRgb(p, q, h + 1/3);
        g = hueToRgb(p, q, h);
        b = hueToRgb(p, q, h - 1/3);
    }
    
    const toHex = (x: number) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Convert a hex color to an `rgba(...)` string at the given alpha - used for
 *  brand-derived glows/borders that previously were hardcoded to blue. */
function hexToRgba(hexColor: string, alpha: number): string {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface DirmShaftPatternProps {
    seq: number;
}

interface ShaftButton {
    assignmentId: number;
    floor: string;
    label: string;
    color: string;
    isSubfloor: boolean;
    requiresAuth: boolean;
    visibility_auth_tier: number;
    target_dir_sequence_num: number | null;
    target_directory_name: string | null;
    target_header_text: string | null;
    prefix: string;
    child_sequence_num: number;
    tsx_file_name_target: string;
    directRoute: string | null;
}

const DirmShaftPattern: React.FC<DirmShaftPatternProps> = ({ seq }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const chrome = useNavigationChrome('shaft', seq); // Database-driven shaft chrome
    const { collection } = useBrandingContext(); // App-level branding for color family

    // Generate color family from primaryColor (or fallback to theme default)
    const primaryColor = collection?.primaryColor || '#1976d2'; // MUI primary.main
    const colorFamily = useMemo(() => {
        return generateColorFamily(primaryColor, 15); // Generate 15 color gradations
    }, [primaryColor]);
    // Darkest gradation in the family - used for brand-derived gradients/glows
    // (previously these decorative accents were hardcoded to a fixed blue
    // regardless of the org's actual brand collection, producing an off-brand
    // mismatch for red/indigo/grey themes).
    const darkAccentColor = colorFamily[colorFamily.length - 1] || '#0d4fb3';

    // 🔒 LOCKED PATTERN: useDirectoryHost handles all directory resolution
    // (manifest-based for nested dirs, component-based fallback for direct access)
    const dir = useDirectoryHost(seq);

    // Map manifest buttons → shaft buttons, sorted by display_order
    const shaftButtons = useMemo(() => {
        if (!dir) return [];
        return dir.buttons
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(
                (btn): ShaftButton => {
                    const isDirectory = btn.target_dir_sequence_num != null;
                    return {
                        assignmentId: btn.assignmentId,
                        floor: String(btn.floorNumber ?? '0').padStart(2, '0'),
                        label: isDirectory
                            ? (btn.target_directory_name || btn.target_header_text || btn.displayLabel)
                            : btn.displayLabel,
                        // Single consistent brand color for all buttons (no per-button
                        // gradation) - colorFamily is still computed above and kept
                        // available for potential reuse elsewhere (e.g. branding collection).
                        color: btn.iconColor || primaryColor,
                        isSubfloor: !!(btn.subfloorCode && btn.subfloorCode.trim() !== ''),
                        requiresAuth: btn.visibility_auth_tier > 0,
                        visibility_auth_tier: btn.visibility_auth_tier,
                        target_dir_sequence_num: btn.target_dir_sequence_num ?? null,
                        target_directory_name: btn.target_directory_name ?? null,
                        target_header_text: btn.target_header_text ?? null,
                        prefix: btn.prefix,
                        child_sequence_num: btn.child_sequence_num,
                        tsx_file_name_target: btn.tsx_file_name_target,
                        directRoute: btn.directRoute,
                    };
                },
            );
    }, [dir, primaryColor]);

    // Separate floor 0 (no separator) from floor > 0 (with separators)
    const { floor0Buttons, floorGroups } = useMemo(() => {
        const floor0: ShaftButton[] = [];
        const groups: Record<string, ShaftButton[]> = {};
        
        for (const btn of shaftButtons) {
            const floorNum = parseInt(btn.floor, 10);
            if (floorNum === 0) {
                floor0.push(btn);
            } else {
                if (!groups[btn.floor]) groups[btn.floor] = [];
                groups[btn.floor].push(btn);
            }
        }
        
        return { floor0Buttons: floor0, floorGroups: groups };
    }, [shaftButtons]);

    if (!dir) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error">Elevator {seq} not found in manifest.</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: chrome.shaft.container.minHeight,
                width: '100%',
                // Professional directory access background (shared with floor access pattern)
                backgroundImage: `url(${elevatorBackgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                position: 'relative',
                overflow: 'hidden',
                pr: { xs: 2, md: 8 },
            }}
        >
            {/* Graphite rail overlay - matches Techgacity design system */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(27, 26, 23, 0.05)',
                    zIndex: 1,
                }}
            />

            {/* Elevator Panel */}
            <Paper
                elevation={24}
                sx={{
                    width: { xs: '100%', sm: 340, md: 380 },
                    maxWidth: 380,
                    background: dir.panelBackground || '#fdfdf9',
                    borderRadius: 4,
                    border: `3px solid ${dir.borderColor || hexToRgba(primaryColor, 0.5)}`,
                    boxShadow: `0 20px 60px ${hexToRgba(primaryColor, 0.3)}, inset 0 0 40px ${hexToRgba(primaryColor, 0.05)}`,
                    overflow: 'hidden',
                    position: 'relative',
                    zIndex: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                {/* Header */}
                {dir.headerText && (
                    <Box
                        sx={{
                            background:
                                dir.headerGradient ||
                                `linear-gradient(135deg, ${primaryColor} 0%, ${darkAccentColor} 100%)`,
                            p: 1.5,
                            textAlign: 'center',
                            borderBottom: `2px solid ${dir.borderColor || primaryColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                        }}
                    >
                        <Box
                            component="img"
                            src="/branding/techgacity-mark.svg"
                            alt="TechGacity mark"
                            sx={{
                                width: 28,
                                height: 28,
                                flexShrink: 0,
                                filter: 'brightness(0) invert(1)',
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: 'white',
                                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            }}
                        >
                            {dir.headerText}
                        </Typography>
                    </Box>
                )}

                {/* Elevator Buttons by Floor */}
                <Box sx={{ p: 1.5 }}>
                    {shaftButtons.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ color: 'rgba(19, 17, 9, 0.5)', fontSize: '0.8rem' }}>
                                No floors registered.
                            </Typography>
                            <Typography sx={{ color: 'rgba(19, 17, 9, 0.3)', fontSize: '0.7rem', mt: 0.5 }}>
                                Assign buttons via Directory Builder.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Floor 0 buttons — Public Directories separator */}
                            {floor0Buttons.length > 0 && (
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                        color: primaryColor,
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        mb: 0.5,
                                        mt: 0.5,
                                        py: 0.3,
                                    }}
                                >
                                    ----- Public Directories -----
                                </Typography>
                            )}
                            {floor0Buttons.map((btn) => {
                                // Find original manifest button by assignmentId
                                const manifestBtn = dir.buttons.find(b => b.assignmentId === btn.assignmentId);
                                if (!manifestBtn) return null;
                                
                                return (
                                <Button
                                    key={btn.assignmentId}
                                    fullWidth
                                    onClick={() => handleManifestButtonClick(
                                        manifestBtn,
                                        navigate,
                                        dir.directorySequenceNum,
                                        location.pathname
                                    )}
                                    sx={{
                                        mb: 0.5,
                                        py: btn.isSubfloor ? 0.6 : 0.8,
                                        pl: btn.isSubfloor ? 3 : 2,
                                        pr: 4,
                                        position: 'relative',
                                        background: `linear-gradient(135deg, ${btn.color} 0%, ${btn.color}cc 100%)`,
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: btn.isSubfloor ? '0.7rem' : '0.75rem',
                                        textAlign: 'left',
                                        justifyContent: 'flex-start',
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateX(8px)',
                                            boxShadow: `0 4px 16px ${hexToRgba(primaryColor, 0.5)}`,
                                        },
                                    }}
                                >
                                    {btn.label}
                                    <Tooltip title={btn.label} placement="left" arrow>
                                        <RouteIcon
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                fontSize: 14,
                                                color: 'rgba(255,255,255,0.4)',
                                                transition: 'color 0.2s',
                                                '&:hover': { color: 'rgba(255,255,255,0.8)' },
                                            }}
                                        />
                                    </Tooltip>
                                </Button>
                                );
                            })}

                            {/* Floor > 0 buttons — with floor number separator */}
                            {Object.entries(floorGroups)
                                .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                                .map(([floor, buttons]) => {
                                    return (
                                    <Box key={floor} sx={{ mb: 1 }}>
                                        {/* Floor separator - shown for all floors > 0 */}
                                        <Typography
                                            sx={{
                                                textAlign: 'center',
                                                color: primaryColor,
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                mb: 0.5,
                                                mt: 1,
                                                py: 0.3,
                                            }}
                                        >
                                            -------- {floor} --------
                                        </Typography>
                                        {buttons.map((btn) => {
                                            // Find original manifest button by assignmentId
                                            const manifestBtn = dir.buttons.find(b => b.assignmentId === btn.assignmentId);
                                            if (!manifestBtn) return null;
                                            
                                            return (
                                            <Button
                                                key={btn.assignmentId}
                                                fullWidth
                                                onClick={() => handleManifestButtonClick(
                                                    manifestBtn,
                                                    navigate,
                                                    dir.directorySequenceNum,
                                                    location.pathname
                                                )}
                                            sx={{
                                                mb: 0.5,
                                                py: btn.isSubfloor ? 0.6 : 0.8,
                                                pl: btn.isSubfloor ? 3 : 2,
                                                pr: 4,
                                                position: 'relative',
                                                background: `linear-gradient(135deg, ${btn.color} 0%, ${btn.color}cc 100%)`,
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: btn.isSubfloor ? '0.7rem' : '0.75rem',
                                                textAlign: 'left',
                                                justifyContent: 'flex-start',
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateX(8px)',
                                                    boxShadow: `0 4px 16px ${hexToRgba(primaryColor, 0.5)}`,
                                                },
                                            }}
                                        >
                                            {btn.label}
                                            <Tooltip title={btn.label} placement="left" arrow>
                                                <RouteIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 6,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        fontSize: 14,
                                                        color: 'rgba(255,255,255,0.3)',
                                                        opacity: 0.4,
                                                        transition: 'opacity 0.2s, color 0.2s',
                                                        '&:hover': {
                                                            opacity: 1,
                                                            color: 'white',
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        </Button>
                                        );
                                    })}
                                </Box>
                                );})}
                        </>
                    )}
                </Box>
            </Paper>

            {/* Directory identity footer */}
            <Typography
                variant="caption"
                sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    color: 'rgba(241, 236, 224, 0.5)',
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    zIndex: 2,
                }}
            >
                {dir.directoryName} • seq:{dir.componentSequenceNum} • {dir.tsxFileName}
            </Typography>
        </Box>
    );
};

export default DirmShaftPattern;
